import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Ruler, Clock, Shovel } from "lucide-react";
import { PHASE_MAP, phaseBadge } from "@/lib/phases";
import { SitePlaceholder } from "@/components/SitePlaceholder";

export const PyramidCard = ({ p, index = 0 }) => {
  const hasDeepDive = !!p.research;
  const ph = PHASE_MAP[p.phase] || PHASE_MAP.pyramids;
  const src = p.gallery?.[0];
  const [broken, setBroken] = useState(false);
  const showImg = src && !broken;

  return (
    <Link
      to={`/pyramid/${p.slug}`}
      data-testid={`pyramid-card-${p.slug}`}
      className="group relative flex flex-col overflow-hidden bg-card border border-border hover:border-accent/50 transition-colors duration-300 hover:-translate-y-1 shadow-sm hover:shadow-xl fade-up"
      style={{ animationDelay: `${Math.min(index, 12) * 50}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
        {showImg ? (
          <img
            src={src}
            alt={p.name}
            loading="lazy"
            onError={() => setBroken(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <SitePlaceholder phase={p.phase} className="h-full w-full" />
        )}
        {showImg && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />}
        <span className="absolute top-3 left-3 bg-black/80 text-amber-400 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border border-amber-500/30 backdrop-blur-sm">
          {p.country}
        </span>
        <span
          className="absolute top-3 right-3 flex items-center gap-1.5 text-white font-mono text-[9px] uppercase tracking-widest px-2 py-1 backdrop-blur-sm"
          style={{ background: `${ph.color}E6` }}
        >
          {phaseBadge(p.phase)}
        </span>
        {hasDeepDive && (
          <span
            data-testid={`deep-dive-badge-${p.slug}`}
            title="Deep dive available"
            className="absolute bottom-3 left-3 flex items-center gap-1 bg-accent text-accent-foreground font-mono text-[9px] uppercase tracking-widest px-2 py-1 shadow"
          >
            <Shovel className="h-2.5 w-2.5" /> Deep Dive
          </span>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1 gap-4">
        <div>
          <h3 className="font-serif text-2xl font-bold leading-tight text-foreground group-hover:text-accent transition-colors">
            {p.name}
          </h3>
          <p className="mt-1 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            <MapPin className="h-3 w-3" /> {p.region || p.country}
          </p>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{p.summary}</p>
        <div className="mt-auto grid grid-cols-2 gap-2 border-t border-border pt-3 font-mono text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><Ruler className="h-3 w-3 text-accent" /> {p.height_m ? `${p.height_m} m` : "—"}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-accent" /> {p.estimated_age ? p.estimated_age.split("(")[0].trim() : "Undated"}</span>
        </div>
      </div>
    </Link>
  );
};
